import ConvertIds from '@atlas/atlas-convert-ids';
import propCache from '@atlas/prop-cache';
import { debounce, isString } from 'lodash';
import { SnapPointBaseElement, SnapPointSelectionObject } from './SnapPointBaseElement';
import {
    DISPLAY_DELAY,
    SnapOptions,
    generateSelObj,
    getCachedPoints,
    getDefaultSnapOption,
    getPointCacheRegistered,
    isCurveFromPsId,
    isPointFromPsId,
    registerPointCache
} from './atlasSelectionUtils';
import markerUtils from './markerUtils';

const _ = { debounce: debounce, isString };

/**
 * @typedef {object} PointLocData
 * @property {string} psId
 * @property {{location: import('./atlasSelectionUtils').Point3d, type: string}[]} locs
 */
/**
 * @typedef {{ box?:THREE.Box2, points: import('./atlasSelectionUtils').LocData[] }} PointData
 */

let processedFaces = {};
let controlManager = null;
let viewer = null;
let PLMVisWeb = null;
/** @type {HTMLElement} */
let svgOverlay = null;
/** @type {Map<string, PointData>} */
let activeEdges = new Map();
let SPLM = null;
let sendApi = null;
/** @type {import('./AtlasSelection.js').AtlasSelection} */
let atlasSelection = null;
let apiId = 0;
let sgoMgr = null;
let sgoUtils = null;

//TODO: Deactivating/deleting element should remove listener from ODD

let preselId = null;
const dist = (loc1, loc2) => {
    return Math.sqrt(Math.pow(loc1.x - loc2.x, 2) + Math.pow(loc1.y - loc2.y, 2));
};
/** @type {import('./atlasSelectionUtils').LocData} */
let highlightLoc = null;
let markRadius = 1;
const removeFromActive = (psId, force) => {
    let edgePoints = activeEdges.get(psId);
    let clearActive = true;
    if (edgePoints && edgePoints.points) {
        for (let loc of edgePoints.points) {
            if (loc.floating) {
                if (!force) {
                    clearActive = false;
                    break;
                } else if (loc.marker && loc.marker.classList[0] === 'snap-point-marker') {
                    svgOverlay.removeChild(loc.marker);
                    loc.marker = null;
                }
            }
        }
    }
    if (clearActive) activeEdges.delete(psId);
};

let camChange = false;

const getPointOnEdgeRule = (edgePointObj) => {
    let splmApiCached = SPLM.api;
    SPLM.api = [];
    let result = {};

    let edgeParam = 0;
    if (edgePointObj.type === 'Point') {
        const pointProtoId = ConvertIds.toPrototype(edgePointObj.parentId);
        result.point = new SPLM.Point(pointProtoId);
    } else {
        if (edgePointObj.type === 'Start') {
            edgeParam = 0;
        } else if (edgePointObj.type === 'Mid') {
            edgeParam = 0.5;
        } else if (edgePointObj.type === 'End') {
            edgeParam = 1;
        }
        let theSession = new SPLM.Session();
        let workPart = theSession.Parts.Work;

        const edgeProtoId = ConvertIds.toPrototype(edgePointObj.parentId);
        const startEdge = new SPLM.Edge(edgeProtoId);
        if (edgePointObj.type === 'Center') {
            result.point = workPart.Points.CreatePointEdgeCenter(startEdge, SPLM.SmartObject.UpdateOption.WithinModeling);
        } else {
            let scalar = workPart.Scalars.CreateScalar(edgeParam, SPLM.Scalar.DimensionalityType.None, SPLM.SmartObject.UpdateOption.WithinModeling);
            result.point = workPart.Points.CreatePointOnCurve(startEdge, scalar.result(), SPLM.SmartObject.UpdateOption.WithinModeling);
        }
    }
    result.api = SPLM.api;

    SPLM.api = splmApiCached;

    return result;
};

const getPointByPsId = (viewer, psId) => {
    let ptLocArr = viewer.getPointsByPsId(psId);
    let ptLoc = undefined;
    if (ptLocArr && ptLocArr[0]) {
        ptLoc = { x: ptLocArr[0][0], y: ptLocArr[0][1], z: ptLocArr[0][2] };
    }
    return ptLoc;
};

/**
 * Gets the "sequence counter" of the splie pole or defining points or snapPointExtension point
 *
 * @param {object} loc - snapPoint location data
 * @returns {number} - snap point sequence id
 */
const getCounter = (loc) => {
    let counter = undefined;
    if (loc.type === 'Pole') {
        counter = loc.poleSeqId;
    } else if (loc.type === 'DefiningPoint') {
        counter = loc.defPointSeqId;
    } else if (loc.subtype === 'SnapPointExtension') {
        counter = loc.counter;
    }
    return counter;
};

/**
 * SnapPointExtension is an abstraction clients can inherit from to add new snap types to SnapPointElement.
 *
 * @class SnapPointExtension
 */
export class SnapPointExtension {
    constructor() {}

    /**
     * getPickingMask will get called when the SnapPointElement is made active.  Return the PLMVisWeb.PickingMask types
     * desired.  They can be or'ed, as in 'return PickingMask.FACE | PickingMask.DisplayEntity'.  These types will be
     * added to the types SnapPointElement usually uses, ie they will not interfere with default behavior.
     *
     * @returns {number}
     */
    getPickingMask() {
        throw new Error('Subclasses of SnapPointExtension must implement getPickingMask');
    }

    /**
     * getPointsAtScreenLocation gets called when over any object allowed by the specified in getPickingMask.
     * a) SnapPointExtension can elect the return any or all of the input cached edgePoints based on your workflow
     * b) SnapPointExtension can elect to return your specified point
     * c) SnapPointExtension can elect to return any or all of the input cached edgePoints and your specified point
     *
     * @param {object} event - the original mouse move event passed to atlas-selection
     * @param {object[]} curObjects - Array of objects returned by PLMVisWeb at the location in question
     * @param {object[]} edgePoints - Cached array of point objects on the edge
     * @returns {object} - This object must have a member 'psId' (the psId of the object associated with the point) and
     *                    a member 'locs', an array of objects.  Each object of the locs array must have a member 'location',
     *                    an object with the members 'x' 'y' and 'z', the location of the point in prototype space; and a member 'type'
     *                    which is an arbitrary string you need to populate but can be any string that helps you distinguish each location
     *                    in the locs array.
     */
    getPointsAtScreenLocation(event, curObjects, edgePoints) {
        throw new Error('Subclasses of SnapPointExtension must implement getPointsAtScreenLocation');
    }
}

/**
 * SnapPointElement constructor.  This element allows snap point selection.  It behaves like any other
 * SelectionElement in that it generates selection objects that are added onto the selection list.  These
 * selection objects contain the precise point xyz in the result member, and the SDK objects necessary for
 * Atlas to generate the selected point in the recipe member.
 */
export class SnapPointElement extends SnapPointBaseElement {
    /**
     * @class SnapPointElement
     * @param {object}   controlManagerInp - The PLMVisWeb control manager.  It is used to access the viewer
     *                                       dimensions, and to gain access to the Viewer object in use.
     * @param {object}   PLMVisWebInp      - The PLMVisWeb module itself
     * @param {HTMLElement} svgOverlayInp  - The SVG DOM element that overlays the PLMVisWeb canvas.  This element
     *                                       must be supplied by the application.  Galaxy already provides and maintains
     *                                       this element. Due to performance considerations, this element uses SVG for
     *                                       prehighlighing of the snap point locations.
     * @param {object}   SPLMInp           - The SPLM SDK object, which must have the point construction APIs defined.
     * @param {object}   Sphere            - An object that provides sphere (sgo) constructor
     * @param {object}   sgoApi            - An object that provides api for sgo, i.e. mouse over, hightlight.
     * @param {Function} sendApiInp        - A function that will send an API message to Atlas asynchronously. This function is
     *                                       expected to take one input object { API: xxx, ApiDescription: yyy }:
     *                                            1) xxx: the array of SPLM SDK instructions (SPLM.api),
     *                                            2) yyy: the description for the API which is used as API message Id.
     *                                       It should return a promise that resolves with the api result from Atlas.
     *                                       Galaxy already provides this function.
     * @param {import('./AtlasSelection.js').AtlasSelection} atlasSelInp         - An instance of AtlasSelection object
     */
    constructor(controlManagerInp, PLMVisWebInp, svgOverlayInp, SPLMInp, Sphere, sgoApi, sendApiInp, atlasSelInp) {
        super(controlManagerInp, PLMVisWebInp, svgOverlayInp, SPLMInp, Sphere, sendApiInp, atlasSelInp);
        controlManager = controlManagerInp;
        viewer = controlManagerInp.viewer;
        PLMVisWeb = PLMVisWebInp;
        svgOverlay = svgOverlayInp;
        SPLM = SPLMInp;
        sendApi = sendApiInp;
        atlasSelection = atlasSelInp;
        markRadius = this._markRadius;
        sgoMgr = super.sgoMgr;
        sgoUtils = sgoApi;
        /** @type {SnapPointExtension[]} */
        this._extensions = [];
        this._snapOption = getDefaultSnapOption(); // use the default snap option
    }
    addExtension(extension) {
        if (!(extension instanceof SnapPointExtension)) {
            throw new Error('SnapPoint extensions must inherit from SnapPointExtenstion and implement every method');
        }
        if (!this._extensions.includes(extension)) {
            this._extensions.push(extension);
        }
    }

    removeExtension(extension) {
        const idx = this._extensions.indexOf(extension);
        if (idx > -1) {
            this._extensions.splice(idx, 1);
        }
    }
    getExtensions() {
        return this._extensions;
    }
    get active() {
        return super.active;
    }
    set active(newActive) {
        if (newActive) {
            // this gets called before any part is loaded in the sample app,
            // so I'm leaving the check for children in PLMVW
            if (this.isStateful) {
                if (!getPointCacheRegistered() && viewer.getProductStructureInfo().children) {
                    registerPointCache(SPLM);
                }
            }
        }
        super.active = newActive;
    }
    async onMouseMove(e, curObjects) {
        if (!this.isStateful) {
            return undefined;
        }
        delayPrehighlightLoc.cancel();

        let mouseLoc = { x: e.offsetX, y: e.offsetY };
        let result = null;
        /* if cur mouse loc out of bounding box of pts, remove */
        for (let [psId, oldPtData] of activeEdges) {
            let range = 20;
            if (oldPtData.box) {
                let boxSize = new PLMVisWeb.THREE.Vector2();
                oldPtData.box.getSize(boxSize);
                range = boxSize.length() * 0.3;
                if (range > 20) range = 20;
            }
            if (oldPtData.box && oldPtData.box.distanceToPoint(new PLMVisWeb.THREE.Vector2(mouseLoc.x, mouseLoc.y)) > range) {
                removeFromActive(psId, true);
            }
        }
        if (curObjects) {
            // eventually we need to handle more complicated case, where
            // point candidate fails validation and another underneath passes
            for (let obj of curObjects) {
                if (obj.isBlocked) break;
                // need to check if this display entity is really a curve, as many objects
                // are represented by display entities.  Specific case was a datum plane in front of a curve.
                if (
                    obj.type === 'edge' ||
                    (obj.type === 'DisplayEntity' && isCurveFromPsId(viewer, obj.id)) ||
                    (obj.type === 'DisplayEntity' && isPointFromPsId(viewer, obj.id))
                ) {
                    result = obj;
                    break;
                }
            }
        }
        // are we over a location we're tracking?
        let locTol = markRadius * 1.5;
        let newLoc = null;
        let retObj = undefined;
        let foundSelected = false;
        for (let [psId, edgeLocs] of activeEdges) {
            for (let loc of edgeLocs.points) {
                // after change to delay display of markers, we need to check loc.marker here.  If
                // we prehighlight before markers are displayed, then other markers on edge will
                // never be displayed, because we never get to next block of code where we decide
                // we're over an edge.
                // scenario where this happens: roll over circular edge, we hold on to points in case
                // user moves to center, if instead user rolls directly over any other point (which isn't
                // displayed) then that marker was getting highlighted (from highlight()) and only that marker
                // was getting created.  By avoiding locations without a marker, we force logic thru the block
                // below where we're over the edge, then markers get displayed and everything works as expected.
                if (dist({ x: loc.xPx, y: loc.yPx }, mouseLoc) < locTol && (loc.marker || loc.sgo)) {
                    foundSelected = true;
                    delayPrehighlightLoc(loc, e, svgOverlay, psId, edgeLocs, mouseLoc);
                    newLoc = loc;
                    // we found a location.  But it's possible this isn't the one
                    // atlas-selection thinks we're over, or atlas-selection may not
                    // have a candidate at all.  So we have to tell atlas-selection about
                    // our candidate.  Details of what all this object should be need to be
                    // sorted out, for the moment try to match curObjects
                    if (!result || result.id !== psId) {
                        // don't add a duplicate
                        retObj = { id: psId, edgeId: psId, isBlocked: false, type: 'edge' };
                    }
                }
            }
        }
        if (!foundSelected) {
            // We didn't find any selected SnapPoint under the cursor, go back to do pre-pre-highlight

            // In general, SnapPointExtension may not follow regular SnapPoint logics. For example, it may or
            // may not be on an edge. In this case, result will be null. When this happens, we'll never be able
            // to get to the SnapPointExtension logics below if we want to input edgePoints into it.
            // We also don't want to say if extension function exists, we go to the SnapPoint logics since, even
            // extension function exists, the extension may not return valid result.
            // Because of this, we do an evaluation of the extension below first to see if there are any valid
            // result, if there is, we say result found and go further.
            let extendedLocs = undefined;
            for (let ext of this._extensions) {
                extendedLocs = ext.getPointsAtScreenLocation(e, curObjects, undefined);
                if (extendedLocs) {
                    result = { id: extendedLocs.psId };
                    break;
                }
            }
            if (!result) {
                if (preselId) {
                    delayDisplayMarkers.cancel();
                    markerUtils.clearMarkers(svgOverlay, true);
                    removeFromActive(preselId);
                }
                preselId = null;
            } else {
                let psId = result.id;
                let atlasId = ConvertIds.fromPsId(viewer, psId, true);
                let edgePoints = undefined;
                if (isPointFromPsId(viewer, psId)) {
                    //let loc = viewer.getPointByPsId(psId);
                    let loc = getPointByPsId(viewer, psId);
                    if (loc) {
                        edgePoints = [
                            { floating: false, location: loc, type: 'Point', parentId: atlasId, parentPsId: psId, isPrototypeSpace: false }
                        ];
                    }
                } else {
                    const snapOption = this.getSnapOption();
                    edgePoints = getCachedPoints([JSON.stringify(atlasId)], snapOption)[0];
                }
                // Use edgePoints as input and see if point extension has any valid result. If it has, it takes
                // the precedence. Otherwise, the original logics stays
                extendedLocs = undefined;
                for (let ext of this._extensions) {
                    extendedLocs = ext.getPointsAtScreenLocation(e, curObjects, edgePoints);
                    if (extendedLocs) {
                        result = { id: psId };
                        for (let idx = 0; idx < extendedLocs.locs.length; idx++) {
                            let loc = extendedLocs.locs[idx];
                            loc.subtype = 'SnapPointExtension'; // Hide this from SnapPointExtension extension writer
                        }
                        break;
                    }
                }
                if (extendedLocs) {
                    for (let i = 0; i < extendedLocs.locs.length; i++) {
                        let loc = extendedLocs.locs[i];
                        //validate output of extension
                        if (!loc || !loc.location || loc?.location.x === undefined || loc.location.y === undefined || loc.location.z === undefined) {
                            throw new Error(
                                'Member of locs array must have a member "location" that is an object with x, y, and z members (prototype XYZ)'
                            );
                        }
                        if (!loc.type || !_.isString(loc.type)) {
                            throw new Error('Member of locs array must have a member "type" that is a string');
                        }
                        // is psId already active?  If so we need to reuse the objects
                        // already defined.  Otherwise new objects will overwrite what's in
                        // activeEdges, and that will result in new markers getting created.
                        // then there multiple markers left around with locations that activeEdges
                        // no longer knows about.  This isn't a problem in normal points because they're
                        // always cached, but extentions can and probably will create new objects.
                        let activePoints = activeEdges.get(psId);
                        if (activePoints) {
                            for (let activeLoc of activePoints.points) {
                                if (activeLoc.type === loc.type) {
                                    extendedLocs.locs[i] = activeLoc;
                                    extendedLocs.locs[i].location = loc.location;
                                }
                            }
                        }
                    }
                    edgePoints = extendedLocs.locs;
                }
                let pointData = { points: edgePoints };
                if (edgePoints) {
                    for (let point of edgePoints) {
                        point.parentPsId = psId;
                        point.parentId = atlasId;
                    }
                    // The coordinates of the edgePoints are in the prototype space. The point locations need to be
                    // transformed to the world space when in assembly context.
                    // We use "isPrototypeSpace" property to be "true" in each point to flag this.
                    for (let point of pointData.points) {
                        if (point.isPrototypeSpace === undefined) {
                            // This is the place to define if the point is from prototype. Point location from psId is
                            // the only case that the point is NOT defined in prototype.
                            point.isPrototypeSpace = true;
                        }
                    }
                    delayDisplayMarkers.cancel();
                    // displayPoints is an async function and it adds marker to pointData. await is needed here.
                    await displayPoints(psId, pointData, mouseLoc);
                    if (psId !== preselId) {
                        removeFromActive(preselId);
                        preselId = psId;
                    }
                    activeEdges.set(psId, pointData);
                } else {
                    let faceIds = viewer.getFaceIdsById(psId);
                    propCache.cacheByPsIdsDelayedCount(faceIds);

                    if (preselId) {
                        delayDisplayMarkers.cancel();
                        markerUtils.clearMarkers(svgOverlay, true);
                        removeFromActive(preselId);
                    }
                    preselId = null;
                }
            }
        }
        // preselId - currently displaying points
        // activeEdges - those edges we're holding onto for center points

        if (highlightLoc !== newLoc) {
            if (highlightLoc && highlightLoc.marker) markerUtils.onMouseOut(highlightLoc.marker, e);
            if (highlightLoc && highlightLoc.sgo) {
                sgoUtils.onMouseOutSGO(highlightLoc.sgo, e);
            }
            highlightLoc = newLoc;
        }

        if (highlightLoc === null) {
            markerUtils.onMouseOut(null, e); // cursor is away from Loc marker. change cursor style back
        }

        return retObj;
    }

    async ofObject(psId) {
        // if we're over a location, return its rule. But need to verify
        // psId, as there could be multiple candidates under the cursor
        if (highlightLoc && highlightLoc.parentPsId === psId) {
            let loc = highlightLoc;
            let counter = getCounter(loc);
            // loc.parentId will only be the prototype.  Need to ensure psId is using
            // same prototype, then create new id using assembly context of psId.
            return generateSelObj(getPointOnEdgeRule, loc, psId, viewer, 'SnapPoint', counter, this, SnapPointSelectionObject);
        }
    }

    getPickingMask() {
        let pickingMask = 0;
        for (let ext of this._extensions) {
            pickingMask |= ext.getPickingMask();
        }
        return pickingMask | PLMVisWeb.PickingMode.EDGE | PLMVisWeb.PickingMode.DISPLAY_ENTITY;
    }
    getDisplayName() {
        return 'SnapPoint';
    }
    getDisplayIcon() {
        return 'point_infer_arc_center';
    }

    /**
     *
     * @param {SnapPointSelectionObject} candidateObject
     * @param {boolean} isHighlight
     * @returns {string[]}
     */
    highlight(candidateObject, isHighlight) {
        let parentPsId = candidateObject.psIds[0];
        let snapPointData = candidateObject.result;
        let mouseLoc = { xPx: snapPointData.xPx, yPx: snapPointData.yPx };

        if (!snapPointData.sgo || (snapPointData.sgo && !snapPointData.sgo.isVisible())) {
            // We ARE NOT on an already selected point (SGO)
            if (!snapPointData.marker) {
                let pointData = { points: [snapPointData] };
                displayPoints(parentPsId, pointData, mouseLoc, true);
            }
            markerUtils.highlightMarker(snapPointData.marker);
            candidateObject.marker = snapPointData.marker;
        } else {
            // We ARE on an already selected point (SGO)
            sgoUtils.highlightSGO(snapPointData.sgo, isHighlight);
        }

        return candidateObject.psIds;
    }
    /**
     * Sets the snap option as defined in SnapOptions
     *
     * @param {number} snapOption - snap option as defined in SnapOptions
     */
    setSnapOption = (snapOption) => {
        const validSnapOption = snapOption >= 0 && snapOption <= SnapOptions.AllSnaps;
        if (validSnapOption) {
            this._snapOption = snapOption;
        }
    };
    /**
     * Returns the snap option as defined in SnapOptions
     *
     * @returns {number} - snap option as defined in SnapOptions
     */
    getSnapOption = () => {
        return this._snapOption;
    };
    /**
     * Gets the SnapPoint selection object
     *
     * @param {string} modelId - PlmVisWeb modelId
     * @param {string[]} atlasId - Atlas Id
     * @param {object} pointData - data related to the snap point
     * @returns {import('./SnapPointBaseElement').SnapPointSelectionObject} - Return a SelectionObject instance if psId is current type
     */
    getSelectionObject = (modelId, atlasId, pointData) => {
        const counter = getCounter(pointData);
        const psId = ConvertIds.toPsId(modelId, atlasId);
        let selObj = generateSelObj(getPointOnEdgeRule, pointData, psId, viewer, 'SnapPoint', counter, undefined, SnapPointSelectionObject);
        return selObj;
    };
}
const prehighlightLoc = (loc, e, svgOverlay, psId, edgeLocs, mouseLoc) => {
    if (loc.sgo) {
        sgoUtils.onMouseOverSGO(loc.sgo, e);
        markerUtils.clearMarkers(svgOverlay); // clear all svg markers when we're on a sgo
    } else {
        if (!loc.marker) {
            displayPoints(psId, edgeLocs, mouseLoc);
            preselId = psId;
        }
        markerUtils.onMouseOver(loc.marker, e);
    }
};
const delayPrehighlightLoc = _.debounce(prehighlightLoc, DISPLAY_DELAY.MOUSE);
/**
 * set current box at boxCenter with the distance of halfBoxDiagonalSize to max and min points
 *
 * @param {object} box2 - 2D box with min/max points
 * @param {object} boxCenter - Vector2 for new box center
 * @param {number} halfBoxDiagonalSize - size of half of box diagonal
 */
const setBox = (box2, boxCenter, halfBoxDiagonalSize) => {
    box2.expandByPoint(new PLMVisWeb.THREE.Vector2(boxCenter.x + halfBoxDiagonalSize, boxCenter.y + halfBoxDiagonalSize));
    box2.expandByPoint(new PLMVisWeb.THREE.Vector2(boxCenter.x - halfBoxDiagonalSize, boxCenter.y - halfBoxDiagonalSize));
};

/**
 *
 * @param {string} psId
 * @param {PointData} pointData
 * @param {object} mouseLoc
 * @param {boolean} immediate
 */
const displayPoints = async function (psId, pointData, mouseLoc, immediate = false) {
    let locs = undefined;
    let worldMatrix = new PLMVisWeb.THREE.Matrix4();
    const worldMatrixArray = viewer.getWorldMatrixByPsId(psId);
    worldMatrix.fromArray(worldMatrixArray);
    if (atlasSelection) {
        let locsFull = pointData.points;
        locs = [];
        let preSelPromises = [];
        for (const loc of locsFull) {
            if (!loc.location) {
                continue;
            }
            // if it clears preselection add it
            let counter = getCounter(loc);
            let selCand = generateSelObj(getPointOnEdgeRule, loc, psId, viewer, 'SnapPoint', counter, undefined, SnapPointSelectionObject);
            let preSelPromise = atlasSelection.processPreSelection([selCand], []).then((selEvent) => {
                if (selEvent.accepted) {
                    selEvent.addCandidates.forEach((locObj) => {
                        locs.push(locObj.result);
                    });
                }
            });
            preSelPromises.push(preSelPromise);
        }
        await Promise.all(preSelPromises);
    } else {
        locs = pointData.points;
    }
    if (locs) {
        pointData.box = new PLMVisWeb.THREE.Box2();
        if (mouseLoc) {
            pointData.box.expandByPoint(new PLMVisWeb.THREE.Vector2(mouseLoc.x, mouseLoc.y));
        }
        for (const loc of locs) {
            if (!loc.location) {
                continue;
            }
            // displayMarkers will eventually set the 2d loc.  But now that it's delayed, that
            // happens much later.  If this isn't called, loc.xPx and yPx are undefined, which gets
            // interpreted as 0,0 which screws up the bounding boxes.
            markerUtils.setLoc2D(PLMVisWeb, viewer, svgOverlay, psId, loc, worldMatrix);
            const loc2d = { x: loc.xPx, y: loc.yPx };
            pointData.box.expandByPoint(new PLMVisWeb.THREE.Vector2(loc2d.x, loc2d.y));
        }
        // adjust the box size if it too small
        let size = new PLMVisWeb.THREE.Vector2();
        pointData.box.getSize(size);
        const diagonal = Math.sqrt(size.height * size.height + size.width * size.width);
        if (diagonal < markRadius) {
            const boxCenter = new PLMVisWeb.THREE.Vector2();
            pointData.box.getCenter(boxCenter);
            setBox(pointData.box, boxCenter, markRadius);
        }
        if (immediate) {
            displayMarkers(locs, psId, worldMatrix);
        } else {
            delayDisplayMarkers.cancel();
            delayDisplayMarkers(locs, psId, worldMatrix);
        }
    }
};

const displayMarkers = (locs, psId, worldMatrix) => {
    markerUtils.clearMarkers(svgOverlay, true);
    for (const loc of locs) {
        if (!loc.location) {
            continue;
        }
        markerUtils.displayMarker(PLMVisWeb, viewer, svgOverlay, markRadius, psId, loc, worldMatrix);
    }
};

const delayDisplayMarkers = _.debounce(displayMarkers, DISPLAY_DELAY.MOUSE);

export default {
    SnapPointElement,
    SnapPointExtension
};
